import React, { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import useThemeMode from "../../helper/hooks/useThemeMode";

const C_LIGHT = {
  bg: "#ffffff", border: "#E5E7EB", text: "#1F2937", muted: "#6B7280",
  accent: "#7C3AED", accentAlt: "#a855f7", accentSoft: "#EDE9FE", hoverBg: "#F9FAFB",
  error: "#DC2626",
};
const C_DARK = {
  bg: "#1a1d26", border: "rgba(255,255,255,0.14)", text: "#e8e9ec", muted: "#a3a8b4",
  accent: "#a78bfa", accentAlt: "#c084fc", accentSoft: "rgba(167,139,250,0.16)", hoverBg: "rgba(255,255,255,0.05)",
  error: "#f87171",
};
// variant="gold" — same cream/gold look as GoldField (the admin Profile form).
const C_GOLD = {
  bg: "#f7f2e8", border: "#eadfcb", text: "#1d1a14", muted: "#7d7466",
  accent: "#b8892c", accentAlt: "#d8b25a", accentSoft: "rgba(184,137,44,0.15)", hoverBg: "rgba(184,137,44,0.08)",
  error: "#c2410c", label: "#2a241b", menuBg: "#fffcf7",
};

/**
 * Drop-in replacement for `<TextField select>` when the option list is long
 * enough to need a search box — a search field is pinned to the top of the
 * dropdown and filters the list live. Options: [{ value, label, icon?,
 * subtitle? }] or plain strings (used as both value and label). `icon`
 * renders inside a small gradient badge next to the label — omit it for a
 * plain text row (e.g. Country/State/City lists). `avatar` (an element) is shown in place of that badge — e.g. a marketplace's own icon.
 *
 *   <SearchableSelect
 *     label="Country" required value={countryIso} onChange={setCountryIso}
 *     options={countries.map(c => ({ value: c.iso2, label: c.name }))}
 *     placeholder="Select country" error={!!fieldErrors.country} helperText={fieldErrors.country}
 *   />
 */
export default function SearchableSelect({
  label, value, onChange, options = [], placeholder = "Select…",
  disabled = false, loading = false, error = false, helperText = "",
  required = false, fullWidth = true, size = "medium", variant, labelIcon: LabelIcon, labelAdornment, searchPlaceholder, sx, renderTrigger,
}) {
  const { isDark } = useThemeMode();
  const isGold = variant === "gold";
  const C = isGold ? C_GOLD : (isDark ? C_DARK : C_LIGHT);

  const normalized = useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );
  const selected = normalized.find((o) => String(o.value) === String(value));

  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);
  const open = Boolean(anchorEl);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [normalized, search]);

  const handleOpen = (e) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
    setSearch("");
    setTimeout(() => searchRef.current?.focus(), 60);
  };
  const handleClose = () => { setAnchorEl(null); setSearch(""); };
  const handlePick = (opt) => { onChange(opt.value); handleClose(); };

  const boxHeight = size === "tiny" ? 34 : size === "small" ? 40 : 44;

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto", ...sx }}>
      {label && (
        <Typography component="div" sx={isGold
          ? { display: "flex", alignItems: "center", gap: 0.9, fontSize: "0.84rem", fontWeight: 700, color: error ? C.error : C.label, mb: 0.9 }
          : { fontSize: "0.72rem", fontWeight: 700, color: error ? C.error : C.muted, mb: 0.5, letterSpacing: "0.02em" }}>
          {isGold && LabelIcon && <LabelIcon sx={{ fontSize: 18, color: C.accent }} />}
          <span>{label}{required && <Box component="span" sx={{ color: C.error }}> *</Box>}</span>
          {isGold && labelAdornment}
        </Typography>
      )}
      {renderTrigger ? renderTrigger({ open, selected, onClick: handleOpen }) : (
      <Box
        onClick={handleOpen} role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-disabled={disabled || undefined} aria-label={typeof label === "string" ? label : undefined} tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) { e.preventDefault(); handleOpen(e); } }}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
          height: boxHeight, px: isGold ? 1.5 : 1.4, borderRadius: isGold ? "999px" : "10px",
          border: `${isGold ? "1.5px" : "1.5px"} solid ${error ? C.error : (open ? C.accent : (isGold ? "#e3c987" : C.border))}`,
          background: isGold ? (open ? "linear-gradient(180deg,#fffaf0,#fdf1d8)" : "linear-gradient(180deg,#fffcf6,#fbf3e1)") : (disabled ? C.hoverBg : C.bg),
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? (isGold ? 0.75 : 0.6) : 1,
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: open ? `0 0 0 3px ${C.accentSoft}` : "none",
          "&:hover": disabled ? {} : { borderColor: error ? C.error : (isGold ? "#d8c49a" : C.accent) },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {selected?.avatar ? selected.avatar : selected?.icon && (
            <Box sx={{
              width: 24, height: 24, borderRadius: "7px", flexShrink: 0,
              background: `linear-gradient(135deg, ${C.accent}, ${C.accentAlt})`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {React.cloneElement(selected.icon, { sx: { fontSize: 14, color: "#fff", ...(selected.icon.props.sx || {}) } })}
            </Box>
          )}
          <Typography component="div" noWrap sx={{ fontSize: "0.86rem", fontWeight: selected ? 600 : 400, color: selected ? C.text : C.muted, minWidth: 0 }}>
            {loading ? "Loading…" : (selected ? (selected.triggerNode || selected.node || selected.label) : placeholder)}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 22, color: isGold ? C.accent : C.muted, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </Box>
      )}
      {!!helperText && (
        <Typography sx={{ fontSize: "0.68rem", color: error ? C.error : C.muted, mt: 0.4, ml: 0.3 }}>
          {helperText}
        </Typography>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.8, borderRadius: isGold ? "24px" : "14px", minWidth: anchorEl?.offsetWidth || 220, maxWidth: 380,
            border: `${isGold ? "1.5px" : "1px"} solid ${isGold ? "#e3c987" : C.border}`,
            background: isGold ? "linear-gradient(180deg,#fffdf8 0%,#fbf3e1 100%)" : (C.menuBg || C.bg),
            boxShadow: isGold ? "0 22px 50px rgba(111,82,18,0.22)" : "0 12px 40px rgba(15,15,25,0.18)",
            overflow: "hidden", position: "relative",
          },
        }}
      >
        <Box sx={isGold ? { p: 1.4, pb: 0.6 } : { p: 1, borderBottom: `1px solid ${C.border}` }}>
          <TextField
            inputRef={searchRef}
            fullWidth size="small" placeholder={searchPlaceholder || (isGold && label ? `Search ${label}…` : "Search…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: C.muted }} /></InputAdornment>,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: isGold ? "999px" : "9px", fontSize: "0.86rem", background: isGold ? "#fffaf0" : C.hoverBg, minHeight: isGold ? 44 : undefined,
                "& fieldset": { borderColor: C.border },
                "&:hover fieldset": { borderColor: C.accent },
                "&.Mui-focused fieldset": { borderColor: C.accent },
                // gold: drop index.css's purple focus ring, keep a gold border.
                ...(isGold ? {
                  "&.Mui-focused": { boxShadow: `0 0 0 3px ${C.accentSoft} !important` },
                  "&.Mui-focused::before": { display: "none !important", animation: "none !important" },
                  "&.Mui-focused fieldset": { borderColor: `${C.accent} !important` },
                } : {}),
              },
            }}
          />
        </Box>
        <Box role="listbox" sx={{ maxHeight: 280, overflowY: "auto", py: 0.5, position: "relative", zIndex: 1, ...(isGold ? { "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#d9b25a", borderRadius: "4px" } } : {}) }}>
          {filtered.length === 0 ? (
            <Box sx={{ px: 2, py: 2.5, textAlign: "center" }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.muted }}>No matches found</Typography>
            </Box>
          ) : (
            filtered.map((opt) => {
              const isSel = String(opt.value) === String(value);
              return (
                <Box
                  key={opt.value}
                  role="option" aria-selected={isSel} aria-disabled={opt.disabled || undefined}
                  onClick={() => { if (!opt.disabled) handlePick(opt); }}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2,
                    px: 1.6, py: opt.icon || opt.avatar ? 0.9 : 1, cursor: opt.disabled ? "not-allowed" : "pointer", opacity: opt.disabled ? 0.5 : 1,
                    background: isSel ? C.accentSoft : "transparent",
                    "&:hover": { background: isSel ? C.accentSoft : C.hoverBg },
                    ...(isGold ? { borderRadius: "16px", mx: 1, my: 0.2, background: isSel ? "#f6e3b4" : "transparent", "&:hover": { background: isSel ? "#f6e3b4" : "rgba(184,137,44,0.10)" } } : {}),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                    {opt.avatar ? opt.avatar : opt.icon && (
                      <Box sx={{
                        width: 30, height: 30, borderRadius: "9px", flexShrink: 0,
                        background: `linear-gradient(135deg, ${C.accent}, ${C.accentAlt})`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: `0 3px 8px ${C.accent}40`,
                      }}>
                        {React.cloneElement(opt.icon, { sx: { fontSize: 16, color: "#fff", ...(opt.icon.props.sx || {}) } })}
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="div" noWrap sx={{ fontSize: "0.84rem", fontWeight: isSel ? 700 : 600, color: isSel ? C.accent : C.text }}>
                        {opt.node || opt.label}
                      </Typography>
                      {opt.subtitle && (
                        <Typography noWrap sx={{ fontSize: "0.7rem", color: C.muted }}>
                          {opt.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {isSel && <CheckIcon sx={{ fontSize: 16, color: C.accent, flexShrink: 0 }} />}
                </Box>
              );
            })
          )}
        </Box>
        {isGold && (
          <Box component="svg" viewBox="0 0 400 60" preserveAspectRatio="none" aria-hidden sx={{ display: "block", width: "100%", height: 34, mt: -1, pointerEvents: "none" }}>
            <defs><linearGradient id="ssWave" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#f5e3b0" /><stop offset="60%" stopColor="#e3b64f" /><stop offset="100%" stopColor="#c99a35" /></linearGradient></defs>
            <path d="M0 40 Q 90 8 190 36 T 400 18 L400 60 L0 60 Z" fill="url(#ssWave)" opacity="0.55" />
          </Box>
        )}
      </Popover>
    </Box>
  );
}
